import React, { useState, useCallback, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AppLayout from './components/Layout/AppLayout';
import FileContainer from './components/Files/FileContainer';
import UploadZone from './components/Upload/UploadZone';
import { filesAPI } from './services/api';
import UploadQueue from './components/Upload/UploadQueue';
import LoginPage from './components/Auth/LoginPage';
import TwoFactorDialog from './components/Auth/TwoFactorDialog';
import TwoFactorSetup from './components/Auth/TwoFactorSetup';
import { usePreferences } from './hooks/usePreferences';
import { useFolderTree, buildFolderPath } from './hooks/useFolders';
import { useUploadQueue } from './hooks/useUploadQueue';
import { useAuth } from './hooks/useAuth';
import SettingsPage from './components/Settings/SettingsPage';
import DashboardPage from './components/Dashboard/DashboardPage';

function FileManagerApp() {
  const queryClient = useQueryClient();
  const { viewMode, setViewMode, sidebarOpen, setSidebarOpen } = usePreferences();
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState('-created_at');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const fileInputRef = useRef(null);

  const { data: folderTree } = useFolderTree();
  const folderPath = buildFolderPath(folderTree || [], selectedFolderId);

  const uploadQueue = useUploadQueue();

  const handleFolderSelect = useCallback((id) => {
    setSelectedFolderId(id);
    setSearch('');
  }, []);

  const handleFilesDropped = useCallback(
    (files) => {
      uploadQueue.addFiles(files, selectedFolderId);
    },
    [uploadQueue, selectedFolderId]
  );

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileDrop = useCallback(async (fileId, targetFolderId) => {
    try {
      await filesAPI.move(fileId, targetFolderId);
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    } catch (err) {
      console.error('Errore spostamento file:', err);
    }
  }, [queryClient]);

  const handleFileInputChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (files?.length > 0) {
        uploadQueue.addFiles(files, selectedFolderId);
      }
      e.target.value = '';
    },
    [uploadQueue, selectedFolderId]
  );

  if (showDashboard) {
    return <DashboardPage onBack={() => setShowDashboard(false)} />;
  }

  if (showAdminSettings) {
    return <SettingsPage onBack={() => setShowAdminSettings(false)} />;
  }

  return (
    <UploadZone onFilesDropped={handleFilesDropped}>
      <AppLayout
        sidebarOpen={sidebarOpen}
        onSidebarToggle={setSidebarOpen}
        selectedFolderId={selectedFolderId}
        onFolderSelect={handleFolderSelect}
        folderTree={folderTree}
        folderPath={folderPath}
        search={search}
        onSearchChange={setSearch}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onUploadClick={handleUploadClick}
        onSettingsOpen={() => setSettingsOpen(true)}
        onAdminSettingsOpen={() => setShowAdminSettings(true)}
        onDashboardOpen={() => setShowDashboard(true)}
        onFileDrop={handleFileDrop}
      >
        <FileContainer
          folderId={selectedFolderId}
          search={search}
          viewMode={viewMode}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onUploadClick={handleUploadClick}
          folderTree={folderTree}
          onFolderSelect={handleFolderSelect}
        />
      </AppLayout>

      {/* Hidden file input for upload button */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* 2FA Settings Dialog */}
      <TwoFactorDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      {/* Upload Queue Panel */}
      <UploadQueue
        items={uploadQueue.items}
        hasActive={uploadQueue.hasActive}
        globalProgress={uploadQueue.globalProgress}
        onCancelItem={uploadQueue.cancelItem}
        onClearCompleted={uploadQueue.clearCompleted}
        onClearAll={uploadQueue.clearAll}
      />
    </UploadZone>
  );
}

function App() {
  const { isAuthenticated, loading, requires2faSetup } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (requires2faSetup) {
    return <TwoFactorSetup />;
  }

  return <FileManagerApp />;
}

export default App;
